import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { Clock, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { showAlert } from '@/slice/AlertSlice';
import { Variants } from '@/types/common';

import {
  useTriggerWebhookMutation,
  WEBHOOK_PUSH_CACHE_KEY
} from '../services/assetApi';

dayjs.extend(localizedFormat);

interface WebhooksTabProps {
  photosetId: string | null;
  photosetName: string;
  lastWebhookTriggeredAt: string | null;
  onClose: () => void;
  // Fires after a 202 so the parent can clear the unsynced badge (backend
  // clears photoset.hasUnsyncedChanges on trigger-webhook).
  onSyncSuccess?: () => void;
  /** Whether the account has a custom webhook endpoint. The page decides
   *  which account (`useWorkspaceAccount`): the session's on /home, the
   *  property's on the internal workspace. */
  hasWebhookEndpoints: boolean;
}

const WebhooksTab = ({
  photosetId,
  photosetName,
  lastWebhookTriggeredAt,
  onClose,
  onSyncSuccess,
  hasWebhookEndpoints
}: WebhooksTabProps) => {
  const dispatch = useDispatch();
  // fixedCacheKey lets the assets page header observe this push's in-flight
  // state (spinning callout icon) without prop threading.
  const [triggerWebhook, { isLoading: sending }] = useTriggerWebhookMutation({
    fixedCacheKey: WEBHOOK_PUSH_CACHE_KEY
  });

  // Photoset takes precedence: "set up an endpoint" advice is misleading when
  // the property fundamentally has no photoset to send. Order matches the
  // disabled-reason precedence on the develop side (#938 follow-up).
  const disabledReason = !photosetId
    ? 'No photo set available for this property.'
    : !hasWebhookEndpoints
    ? 'No webhook endpoint exists for this account.'
    : null;
  const isDisabled = !photosetId || !hasWebhookEndpoints;

  const handleSend = async () => {
    if (!photosetId || !hasWebhookEndpoints) return;
    try {
      await triggerWebhook(photosetId).unwrap();
      onSyncSuccess?.();
      dispatch(
        showAlert({
          type: Variants.SUCCESS,
          message: 'Webhook sent successfully'
        })
      );
      onClose();
    } catch (err) {
      // 409 = a fresh upload's resize/watermark hasn't finished; the webhook
      // is refused until derivatives exist. Anything else stays generic.
      const isStillProcessing =
        (err as { status?: number | string })?.status === 409;
      dispatch(
        showAlert({
          type: Variants.ERROR,
          message: isStillProcessing
            ? 'Photos are still processing — try again in a moment.'
            : 'Failed to send webhook. Please try again.'
        })
      );
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-4">
        <div className="space-y-2 text-sm text-foreground">
          <Typography>
            A webhook will be sent to any active webhook endpoint you have
            created.
          </Typography>
          <Typography>
            The webhook payload includes all active images in this Photo Set (
            <span className="font-semibold">{photosetName}</span>) and any
            additional media links associated with the property if they exist
            (e.g. 3D tours).
          </Typography>
        </div>

        {lastWebhookTriggeredAt && (
          <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>
              Webhook last sent: {dayjs(lastWebhookTriggeredAt).format('LLLL')}
            </span>
          </div>
        )}

        {disabledReason && (
          <div className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
            {disabledReason}
          </div>
        )}

        <Typography variant="small" className="text-muted-foreground">
          Want to learn more about webhooks? Contact{' '}
          <a
            href="mailto:integration@planomatic.com"
            className="text-primary underline"
          >
            integration@planomatic.com
          </a>{' '}
          for more information.
        </Typography>
      </div>

      <div className="flex flex-col-reverse gap-2 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          className="w-full sm:w-auto"
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleSend}
          disabled={isDisabled || sending}
          className="w-full sm:w-auto"
        >
          {sending && <Loader2 className="h-4 w-4 animate-spin" />}
          Send Webhook
        </Button>
      </div>
    </div>
  );
};

export default WebhooksTab;
